/**
 * localStorage helpers pour le Dilemme du jour.
 *
 * - `mp_daily_uuid`  : UUID de session opaque (1 par client/navigateur)
 * - `mp_daily_votes` : [{date, decisionId, optionId}] — historique local
 *                      pour streak + format partage Wordle.
 *
 * Ne pollue jamais le `SaveBlob` du mandat (clé séparée).
 */

const SESSION_KEY = "mp_daily_uuid";
const VOTES_KEY = "mp_daily_votes";

export interface LocalVote {
  date: string;
  decisionId: string;
  optionId: string;
}

function genUuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  // Fallback simple (suffisant pour l'usage anti-bot léger).
  const hex = "0123456789abcdef";
  let out = "";
  for (let i = 0; i < 32; i++) {
    out += hex[Math.floor(Math.random() * 16)];
    if (i === 7 || i === 11 || i === 15 || i === 19) out += "-";
  }
  return out;
}

export function getOrCreateDailyUuid(): string {
  try {
    const cur = localStorage.getItem(SESSION_KEY);
    if (cur && cur.length > 0) return cur;
    const next = genUuid();
    localStorage.setItem(SESSION_KEY, next);
    return next;
  } catch {
    return genUuid();
  }
}

export function loadLocalVotes(): LocalVote[] {
  try {
    const raw = localStorage.getItem(VOTES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function appendLocalVote(vote: LocalVote): LocalVote[] {
  const votes = loadLocalVotes().filter((v) => v.date !== vote.date);
  votes.push(vote);
  votes.sort((a, b) => (a.date < b.date ? -1 : 1));
  try {
    localStorage.setItem(VOTES_KEY, JSON.stringify(votes));
  } catch {
    /* ignore */
  }
  return votes;
}

/**
 * Format partage Wordle pour les 7 derniers jours.
 *
 * 🟦 = jour voté
 * ⬜ = jour non voté
 *
 * Le partage est volontairement minimaliste : pas d'image, pas de QR
 * code, juste du texte copy-pasteable.
 */
export function buildShareString(
  todayIso: string,
  votes: LocalVote[],
  siteUrl: string,
): string {
  const today = new Date(`${todayIso}T00:00:00`);
  const week: string[] = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    week.push(iso);
  }
  const grid = week
    .map((iso) => (votes.some((v) => v.date === iso) ? "🟦" : "⬜"))
    .join("");
  const todayVoted = votes.find((v) => v.date === todayIso);
  const head = todayVoted
    ? `Moi Président · Dilemme du jour · j'ai voté.`
    : `Moi Président · Dilemme du jour.`;
  return `${head}\n${grid}\n${siteUrl}/jour`;
}
